// public/src/components/user/master/PrinterSettingsManagement.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormHelperText
} from '@mui/material';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';
import { PLEDGE_A5_TEMPLATE_MAHARASHTRA } from '../../print/templates/PledgePrintA5MaharashtraPhotoStandard';
import { PLEDGE_A5_TEMPLATE_NO_TEAR } from '../../print/templates/PledgePrintA5NoTear';
import { PLEDGE_A5_TEMPLATE_WITH_TEAR } from '../../print/templates/PledgePrintA5WithTear';
import { RELEASE_A5_MONEY_LENDER } from '../../print/templates/ReleasePrintA5MoneyLender';

const STANDARD_SIZES = ['A4', 'LETTER', 'LEGAL', 'A3', 'A5'];
const DOCUMENT_TYPES = [
  { key: 'pledge', label: 'Pledge' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'pledgeBook', label: 'Pledge Book' },
  { key: 'auctionRegister', label: 'Auction Register' },
  { key: 'remainderLetter', label: 'Remainder Letter' },
  { key: 'preAuctionList', label: 'Pre Auction List' },
  { key: 'saleIntimation', label: 'Sale Intimation Letter' },
  { key: 'dayBook', label: 'Day Book' },
  { key: 'journalRegister', label: 'Journal Register' },
  { key: 'profitLossAccount', label: 'Profit & Loss Account' },
  { key: 'release', label: 'Release receipt' }
];

const PrinterSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [printers, setPrinters] = useState([]);
  const [printersLoading, setPrintersLoading] = useState(false);

  const [formData, setFormData] = useState({
    deviceName: '',
    printerType: 'REGULAR',
    documentSettings: {}
  });

  useEffect(() => {
    fetchSettings();
    fetchPrinters();
  }, []);

  const fetchPrinters = async () => {
    try {
      if (!window?.girviPrint?.getPrinters) return;
      setPrintersLoading(true);
      const result = await window.girviPrint.getPrinters();
      if (result?.ok) {
        setPrinters(result.printers || []);
      } else {
        setPrinters([]);
      }
    } catch (err) {
      console.error('Error fetching printers:', err);
      setPrinters([]);
    } finally {
      setPrintersLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/master/printer-settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success && data.data) {
        setSettings(data.data);
        setFormData({
          deviceName: data.data.deviceName || '',
          printerType: data.data.printerType || 'REGULAR',
          documentSettings: data.data.documentSettings || {}
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching printer settings:', error);
      setSnackbar({ open: true, message: 'Failed to fetch printer settings', severity: 'error' });
      setLoading(false);
    }
  };

  const handlePrinterTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      printerType: e.target.value
    }));
  };

  const handleDeviceNameChange = (e) => {
    setFormData(prev => ({
      ...prev,
      deviceName: e.target.value
    }));
  };

  const handleDocumentSettingChange = (docKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      documentSettings: {
        ...prev.documentSettings,
        [docKey]: {
          ...prev.documentSettings[docKey],
          [field]: value,
          // Reset custom dimensions when switching to STANDARD
          ...(field === 'type' && value === 'STANDARD' ? {
            customHeight: null,
            customWidth: null
          } : {}),
          // Reset standard size when switching to CUSTOM
          ...(field === 'type' && value === 'CUSTOM' ? {
            standardSize: null
          } : {})
        }
      }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Ensure all document settings have default values
      const completeDocumentSettings = {};
      DOCUMENT_TYPES.forEach(doc => {
        const existing = formData.documentSettings[doc.key];
        let standardSize = existing?.standardSize || 'A4';
        
        // A5 is only allowed for pledge and release — reset to A4 if set for other document types
        if (standardSize === 'A5' && doc.key !== 'pledge' && doc.key !== 'release') {
          standardSize = 'A4';
        }
        // Pledge Book: only A4 or A3 allowed for standard size
        if (doc.key === 'pledgeBook' && existing?.type === 'STANDARD' && standardSize !== 'A4' && standardSize !== 'A3') {
          standardSize = 'A4';
        }
        // Pre-Auction List: only A4 or A3 allowed for standard size
        if (doc.key === 'preAuctionList' && existing?.type === 'STANDARD' && standardSize !== 'A4' && standardSize !== 'A3') {
          standardSize = 'A4';
        }
        // Auction Register: only A4 or A3 allowed for standard size
        if (doc.key === 'auctionRegister' && existing?.type === 'STANDARD' && standardSize !== 'A4' && standardSize !== 'A3') {
          standardSize = 'A4';
        }
        
        const row = {
          type: existing?.type || 'STANDARD',
          standardSize: standardSize,
          customHeight: existing?.customHeight || null,
          customWidth: existing?.customWidth || null
        };
        if (doc.key === 'pledge') {
          row.templateVariant =
            standardSize === 'A5' ? (existing?.templateVariant || null) : null;
        }
        if (doc.key === 'release') {
          row.templateVariant =
            standardSize === 'A5' ? (existing?.templateVariant || null) : null;
        }
        completeDocumentSettings[doc.key] = row;
      });

      const response = await fetch(`${baseURL}/master/printer-settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceName: formData.deviceName || null,
          printerType: formData.printerType,
          documentSettings: completeDocumentSettings
        })
      });

      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
        setSnackbar({ open: true, message: 'Printer settings saved successfully', severity: 'success' });
      } else {
        throw new Error(data.message || 'Failed to save settings');
      }
      setSaveLoading(false);
    } catch (error) {
      console.error('Error saving printer settings:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save printer settings', severity: 'error' });
      setSaveLoading(false);
    }
  };

  const getDocumentSetting = (docKey) => {
    const base = {
      type: 'STANDARD',
      standardSize: 'A4',
      customHeight: null,
      customWidth: null
    };
    if (docKey === 'pledge' || docKey === 'release') base.templateVariant = null;
    return { ...base, ...formData.documentSettings[docKey] };
  };

  if (loading) {
    return <MasterPageShell title="Printer Settings" subtitle="Master · Devices" loading />;
  }

  return (
    <MasterPageShell title="Printer Settings" subtitle="Master · Devices">
      <Paper elevation={0} sx={{ ...masterPaperSx, height: 'auto' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 780, color: '#0f172a', mb: 0.5 }}>
          Device & paper sizes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure printer type and paper sizes for different document types
        </Typography>

        <form onSubmit={handleSave}>
          {/* Printer Selection (Desktop) */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Printer Selection
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the Windows printer to use for auto-printing (Electron EXE).
              </Typography>

              {window?.girviPrint?.getPrinters ? (
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Printer</InputLabel>
                  <Select
                    value={formData.deviceName || ''}
                    onChange={handleDeviceNameChange}
                    label="Printer"
                    disabled={printersLoading}
                  >
                    <MenuItem value="">
                      <em>Use Windows Default Printer</em>
                    </MenuItem>
                    {printers.map((p) => (
                      <MenuItem key={p.name} value={p.name}>
                        {p.displayName || p.name} {p.isDefault ? '(Default)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="Printer Name (Windows)"
                  value={formData.deviceName || ''}
                  onChange={handleDeviceNameChange}
                  placeholder="(Optional) Exact Windows printer name for auto-printing"
                />
              )}
            </CardContent>
          </Card>

          {/* Printer Type Selection */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Printer Type
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Printer Type</InputLabel>
                <Select
                  value={formData.printerType}
                  onChange={handlePrinterTypeChange}
                  label="Printer Type"
                >
                  <MenuItem value="REGULAR">Regular Printer</MenuItem>
                  <MenuItem value="DOT_MATRIX">Dot Matrix Printer</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {/* Document Settings */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Document Paper Sizes
          </Typography>

          {DOCUMENT_TYPES.map((doc, index) => {
            const docSetting = getDocumentSetting(doc.key);
            return (
              <Card key={doc.key} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {doc.label}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    {/* Paper Type */}
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Paper Type</InputLabel>
                        <Select
                          value={docSetting.type || 'STANDARD'}
                          onChange={(e) => handleDocumentSettingChange(doc.key, 'type', e.target.value)}
                          label="Paper Type"
                        >
                          <MenuItem value="STANDARD">Standard Size</MenuItem>
                          <MenuItem value="CUSTOM">Custom Size</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Standard Size or Custom Dimensions */}
                    {docSetting.type === 'STANDARD' ? (
                      <Grid item xs={12} md={8}>
                        <FormControl fullWidth>
                          <InputLabel>Standard Size</InputLabel>
                          <Select
                            value={docSetting.standardSize || 'A4'}
                            onChange={(e) => handleDocumentSettingChange(doc.key, 'standardSize', e.target.value)}
                            label="Standard Size"
                          >
                            {STANDARD_SIZES.filter(size => {
                              // Pledge Book: only A4 and A3
                              if (doc.key === 'pledgeBook') return size === 'A4' || size === 'A3';
                              // Pre-Auction List: only A4 and A3 (portrait)
                              if (doc.key === 'preAuctionList') return size === 'A4' || size === 'A3';
                              // Auction Register: only A4 and A3 (landscape)
                              if (doc.key === 'auctionRegister') return size === 'A4' || size === 'A3';
                              // A5 is only available for pledge and release
                              if (size === 'A5' && doc.key !== 'pledge' && doc.key !== 'release') return false;
                              return true;
                            }).map(size => (
                              <MenuItem key={size} value={size}>{size}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Width (cm)"
                            type="number"
                            value={docSetting.customWidth || ''}
                            onChange={(e) => handleDocumentSettingChange(doc.key, 'customWidth', parseFloat(e.target.value) || null)}
                            inputProps={{ min: 1, max: 100, step: 0.1 }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Height (cm)"
                            type="number"
                            value={docSetting.customHeight || ''}
                            onChange={(e) => handleDocumentSettingChange(doc.key, 'customHeight', parseFloat(e.target.value) || null)}
                            inputProps={{ min: 1, max: 100, step: 0.1 }}
                            required
                          />
                        </Grid>
                      </>
                    )}

                    {doc.key === 'pledge' && docSetting.type === 'STANDARD' && (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          disabled={docSetting.standardSize !== 'A5'}
                        >
                          <InputLabel id="pledge-a5-layout-label">Pledge ticket layout (A5)</InputLabel>
                          <Select
                            labelId="pledge-a5-layout-label"
                            label="Pledge ticket layout (A5)"
                            value={
                              docSetting.templateVariant === PLEDGE_A5_TEMPLATE_MAHARASHTRA
                                ? PLEDGE_A5_TEMPLATE_MAHARASHTRA
                                : docSetting.templateVariant === PLEDGE_A5_TEMPLATE_NO_TEAR
                                  ? PLEDGE_A5_TEMPLATE_NO_TEAR
                                  : docSetting.templateVariant === PLEDGE_A5_TEMPLATE_WITH_TEAR
                                    ? PLEDGE_A5_TEMPLATE_WITH_TEAR
                                    : 'A5_PAWN_TICKET'
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              handleDocumentSettingChange(
                                doc.key,
                                'templateVariant',
                                v === 'A5_PAWN_TICKET' ? null : v
                              );
                            }}
                          >
                            <MenuItem value="A5_PAWN_TICKET">
                              A5 — Pawn ticket (English, tear-off)
                            </MenuItem>
                            <MenuItem value={PLEDGE_A5_TEMPLATE_NO_TEAR}>
                              A5 — pledgeticket w/o tear
                            </MenuItem>
                            <MenuItem value={PLEDGE_A5_TEMPLATE_WITH_TEAR}>
                              A5 — pledgeticket with tear
                            </MenuItem>
                            <MenuItem value={PLEDGE_A5_TEMPLATE_MAHARASHTRA}>
                              A5 — Maharashtra standard (photo)
                            </MenuItem>
                          </Select>
                          {docSetting.standardSize !== 'A5' ? (
                            <FormHelperText>
                              Set Standard Size to <strong>A5</strong> above, then choose a pledge ticket layout and Save.
                            </FormHelperText>
                          ) : (
                            <FormHelperText>
                              w/o tear and with tear print A5 portrait. Maharashtra prints A5 landscape (wide).
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {doc.key === 'release' && docSetting.type === 'STANDARD' && (
                      <Grid item xs={12}>
                        <FormControl fullWidth disabled={docSetting.standardSize !== 'A5'}>
                          <InputLabel id="release-a5-layout-label">Release receipt layout (A5)</InputLabel>
                          <Select
                            labelId="release-a5-layout-label"
                            label="Release receipt layout (A5)"
                            value={
                              docSetting.templateVariant === RELEASE_A5_MONEY_LENDER
                                ? RELEASE_A5_MONEY_LENDER
                                : 'A5_RELEASE_SIMPLE'
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              handleDocumentSettingChange(
                                doc.key,
                                'templateVariant',
                                v === 'A5_RELEASE_SIMPLE' ? null : v
                              );
                            }}
                          >
                            <MenuItem value="A5_RELEASE_SIMPLE">A5 — Simple text receipt</MenuItem>
                            <MenuItem value={RELEASE_A5_MONEY_LENDER}>
                              A5 — Money lender style (photos + table)
                            </MenuItem>
                          </Select>
                          {docSetting.standardSize !== 'A5' ? (
                            <FormHelperText>
                              Set Standard Size to <strong>A5</strong> for the money-lender layout, then Save.
                            </FormHelperText>
                          ) : (
                            <FormHelperText>Money-lender layout is A5 portrait with customer and article thumbnails.</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            );
          })}

          {/* Save Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={saveLoading}
            >
              {saveLoading ? <CircularProgress size={24} /> : 'Save Settings'}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MasterPageShell>
  );
};

export default PrinterSettingsManagement;

